import { Injectable } from '@angular/core';
import { ScreenShotService } from './screenShot.service';
import { DrawingsHolderService } from './drawings-holder.service';
import { SceneUtilsService } from '../scene-utils.service';
import { SceneBoxCalculatorService } from './sceneBoxCalculator.service';
import { ProToolsUiManagerService } from '../pro-tools-ui-manager.service';
import { CameraService } from '../camera.service';
import { ZoomService } from '../zoom.service';

declare let THREE: any;

export interface ISize {
  width: number;
  height: number;
}

@Injectable()
export class PrintService {

  public readonly scales = [
    {
      text: '1:1',
      scale: 1
    },
    {
      text: '1/8" = 1ft',
      scale: 96 // 1cm on screen = 96cm in reality
    },
    {
      text: '3/16" = 1 ft.',
      scale: 64
    },
    {
      text: '1/4" = 1ft',
      scale: 48
    },
    {
      text: '1/2" = 1ft',
      scale: 24
    },
    {
      text: '1:100',
      scale: 100
    },
    {
      text: '1:50',
      scale: 50
    },
    {
      text: '1:25',
      scale: 25
    }
  ];

  public readonly paperSizes = [
    {
      text: '8.5" x 11"',
      sizeInCm: { width: 21.59, height: 27.94 }
    },
    {
      text: '11" x 17"',
      sizeInCm: { width: 27.94, height: 43.18 }
    },
    {
      text: '24" x 36"',
      sizeInCm: { width: 60.96, height: 91.44 }
    }
  ];

  public selectedScaleIndex = 3;

  public selectedPaperSizeIndex = 0;

  private oldScale = 0;

  constructor(
    private screenShotSrvc: ScreenShotService,
    private drawingsHolderService: DrawingsHolderService,
    private sceneUtilsService: SceneUtilsService,
    private sceneBoxCalculator: SceneBoxCalculatorService,
    proToolsUiManagerService: ProToolsUiManagerService,
    private cameraSrvc: CameraService,
    private zoomService: ZoomService
  ) {
    proToolsUiManagerService.proToolsModActive.subscribe(active => {
      if (active) {
        const camera = this.cameraSrvc.getCamera();
        this.oldScale = camera.scale.x;
        const box = this.sceneBoxCalculator.calculate();
        const center = new THREE.Vector3();
        box.getCenter(center);
        this.setPaperSize(center);
        this.drawingsHolderService.scale = this.scales[this.selectedScaleIndex];
        this.drawingsHolderService.paperWidth = this.paperSizes[this.selectedPaperSizeIndex].sizeInCm.width;
        this.drawingsHolderService.paperHeight = this.paperSizes[this.selectedPaperSizeIndex].sizeInCm.height;
      } else {
        this.selectedScaleIndex = 3;
        this.selectedPaperSizeIndex = 0;
        this.drawingsHolderService.scale = this.scales[this.selectedScaleIndex];
        this.drawingsHolderService.paperWidth = this.paperSizes[this.selectedPaperSizeIndex].sizeInCm.width;
        this.drawingsHolderService.paperHeight = this.paperSizes[this.selectedPaperSizeIndex].sizeInCm.height;
      }
    });
  }

  private getPaperPixelSize(sceneBoxCenter): ISize {
    const paperSize = this.getPaperSizeInSceneUnits();
    const xOffset = paperSize.width / 2;
    const zOffset = paperSize.height / 2;
    const left = sceneBoxCenter.clone().add(new THREE.Vector3(-xOffset, 0, 0));
    const right = sceneBoxCenter.clone().add(new THREE.Vector3(xOffset, 0, 0));
    const bottom = sceneBoxCenter.clone().add(new THREE.Vector3(0, 0, -zOffset));
    const top = sceneBoxCenter.clone().add(new THREE.Vector3(0, 0, zOffset));
    this.sceneUtilsService.worldToScreen(left);
    this.sceneUtilsService.worldToScreen(right);
    this.sceneUtilsService.worldToScreen(bottom);
    this.sceneUtilsService.worldToScreen(top);
    return { width: left.distanceTo(right), height: bottom.distanceTo(top) };
  }

  get paperSize() {
    return this.paperSizes[this.selectedPaperSizeIndex];
  }

  get scale() {
    return this.scales[this.selectedScaleIndex];
  }

  getPaperSizeInSceneUnits(): ISize {
    if (this.selectedScaleIndex === 0) {
      const box = this.sceneBoxCalculator.calculate();
      const sceneSize = new THREE.Vector3();
      box.getSize(sceneSize);
      return {
        width: sceneSize.x * this.oldScale,
        height: sceneSize.z * this.oldScale
      };
    } else {
      const paperSize = this.paperSize;
      const scale = this.scale;
      return {
        width: paperSize.sizeInCm.height * scale.scale,
        height: paperSize.sizeInCm.width * scale.scale
      };
    }
  }

  calculateScale(): { x: number, y: number } {
    if (this.selectedScaleIndex === 0) {
      return {
        x: this.oldScale,
        y: this.oldScale
      };
    } else {
      const box = this.sceneBoxCalculator.calculate();
      const sceneSize = new THREE.Vector3();
      box.getSize(sceneSize);
      const paperSize = this.getPaperSizeInSceneUnits();
      const x = paperSize.width / sceneSize.x;
      const y = paperSize.height / sceneSize.z;
      return {
        x, y
      };
    }
  }

  private getScreenShot(): string {
    const box = this.sceneBoxCalculator.calculate();
    const center = new THREE.Vector3();
    const { paperBox, canvas } = this.drawingsHolderService;
    box.getCenter(center);
    const size = this.getPaperSizeInSceneUnits();
    paperBox.visible = false;
    const url = this.screenShotSrvc.take(size, center.setY(canvas.getZIndex() + 100));
    paperBox.visible = true;
    this.zoomService.reset();
    return url;
  }

  setPaperSize(center) {
    const paperSize = this.getPaperSizeInSceneUnits();
    const paperBox = this.drawingsHolderService.paperBox;
    paperBox.update(paperSize.width, paperSize.height);
    paperBox.position.copy(center);
  }

  print() {
    const url = this.getScreenShot();
    const win = window.open();
    const doc = win.document;
    const body = doc.body;
    const scaleCorrection = 48 / this.scale.scale;
    const img = <any>doc.createElement('img');
    img.src = url;
    body.style.margin = '0';
    body.style.padding = '0';
    img.decode()
      .then(() => {
        img.width *= scaleCorrection / 3;
        img.height *= scaleCorrection / 3;
        body.appendChild(img);

        win.print();
      })
  }

}



// WEBPACK FOOTER //
// ./src/app/services/proTools/print.service.ts